import React, { useMemo } from 'react';
import styled from 'astroturf/react';
import DownloadIcon from 'js/design_system/icons/arrow_downward.svg';
import BookmarkIcon from 'js/design_system/icons/bookmark_outline.svg';
import BookmarkedIcon from 'js/design_system/icons/bookmark.svg';
import { Paper } from '../../shared/types';
import { Body } from 'js/design_system/Body';
import useNewsInteractions from '../../News/useNewsInteractions';
import { BodyLink } from 'js/design_system/link/BodyLink';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { ImpressionEntity } from 'js/utils/impressions/watchImpressions';
import { getImpressionId } from 'js/utils/impressions/watchImpressions';
import { TextButton } from 'js/design_system/TextButton';
import _RelatedPapersSpinnerContainer from "RelatedPapersSidebar-RelatedPapersSpinnerContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/Reader/RelatedPapersSidebar.tsx?styleId=RelatedPapersSpinnerContainer";
import _RelatedPapersContainer from "RelatedPapersSidebar-RelatedPapersContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/Reader/RelatedPapersSidebar.tsx?styleId=RelatedPapersContainer";
import _RelatedPaperWrapper from "RelatedPapersSidebar-RelatedPaperWrapper.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/Reader/RelatedPapersSidebar.tsx?styleId=RelatedPaperWrapper";
import _RelatedPaperTitle from "RelatedPapersSidebar-RelatedPaperTitle.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/Reader/RelatedPapersSidebar.tsx?styleId=RelatedPaperTitle";
import _RelatedPaperAuthor from "RelatedPapersSidebar-RelatedPaperAuthor.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/Reader/RelatedPapersSidebar.tsx?styleId=RelatedPaperAuthor";
import _ButtonRow from "RelatedPapersSidebar-ButtonRow.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/Reader/RelatedPapersSidebar.tsx?styleId=ButtonRow";
import _PaperTitle from "RelatedPapersSidebar-PaperTitle.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/Reader/RelatedPapersSidebar.tsx?styleId=PaperTitle";


const RelatedPapersSpinnerContainer = /*#__PURE__*/styled("div", null, {
  displayName: "RelatedPapersSpinnerContainer",
  styles: _RelatedPapersSpinnerContainer
});

const RelatedPapersContainer = /*#__PURE__*/styled("div", null, {
  displayName: "RelatedPapersContainer",
  styles: _RelatedPapersContainer
});

const RelatedPaperWrapper = /*#__PURE__*/styled("div", null, {
  displayName: "RelatedPaperWrapper",
  styles: _RelatedPaperWrapper
});

const RelatedPaperTitle = /*#__PURE__*/styled(Body, null, {
  displayName: "RelatedPaperTitle",
  styles: _RelatedPaperTitle
});

const RelatedPaperAuthor = /*#__PURE__*/styled(Body, null, {
  displayName: "RelatedPaperAuthor",
  styles: _RelatedPaperAuthor
});

const ButtonRow = /*#__PURE__*/styled("div", null, {
  displayName: "ButtonRow",
  styles: _ButtonRow
});

const PaperTitle = /*#__PURE__*/styled(BodyLink, null, {
  displayName: "PaperTitle",
  styles: _PaperTitle
});

const appendUrlParams = (url: string, params: Record<string, string>) => {
  const urlObj = new URL(url);
  Object.entries(params).forEach(([key, value]) => {
    urlObj.searchParams.append(key, value);
  });
  return urlObj.toString();
};

function RelatedPaper({
  paper,
  impressionId,
  index,
  updateWorkBookmarkStatus,
}: {
  paper: Paper;
  impressionId: string;
  index: number;
  updateWorkBookmarkStatus: (workId: number, bookmarked: boolean) => void;
}) {
  const { actions, permissions } = useNewsInteractions({
    newsId: paper.id,
    newsType: 'work',
    paperType: null,
    work: paper,
    tags: [],
    updateWorkBookmarkStatus,
    setUserFollowing: () => {},
    removeUserContent: () => {},
    addFeedNews: () => {},
    addFeedComment: () => {},
    removeFeedNews: () => {},
    updateFeedNews: () => {},
    likeFeedNews: () => {},
    unlikeFeedNews: () => {},
    repostFeedNews: () => {},
    unrepostFeedNews: () => {},
    messageText: {
      bookmarkSuccess: 'Saved to library',
      bookmarkFailure: 'Failed to save to library',
      unbookmarkSuccess: 'Removed from library',
      unbookmarkFailure: 'Failed to remove from library',
    },
  });

  const downloadUrl = paper.attachment?.pdfUrl || paper.url;

  function handleDownload() {
    window.open(downloadUrl, '_blank')?.focus();
  }

  const paperUrlWithImpressionTracking = appendUrlParams(paper.url, {
    nav_from: impressionId,
    rw_pos: String(index),
  });

  return (
    <RelatedPaperWrapper>
      <PaperTitle
        href={paperUrlWithImpressionTracking}
        target="_blank"
        size="md"
      >
        {paper.title}
      </PaperTitle>
      <RelatedPaperAuthor size="sm">
        {paper.authors.map((author) => author.displayName).join(', ')}
      </RelatedPaperAuthor>
      <ButtonRow>
        {permissions.isBookmarked ? (
          <TextButton
            inline
            onClick={actions.unbookmarkWork}
            icon={<BookmarkedIcon size={18} />}
          >
            Saved
          </TextButton>
        ) : (
          <TextButton
            inline
            onClick={actions.bookmarkWork}
            icon={<BookmarkIcon size={18} />}
          >
            Save
          </TextButton>
        )}
        <TextButton
          inline
          onClick={handleDownload}
          icon={<DownloadIcon size={18} />}
        >
          Download
        </TextButton>
      </ButtonRow>
    </RelatedPaperWrapper>
  );
}

export default function RelatedPapersSidebar({
  sourceWorkId,
  relatedPapers,
  updateWorkBookmarkStatus,
}: {
  sourceWorkId: number;
  relatedPapers?: Paper[];
  updateWorkBookmarkStatus: (workId: number, bookmarked: boolean) => void;
}) {
  const impressionIds = useMemo(
    () => relatedPapers?.map(() => getImpressionId()) || [],
    [relatedPapers],
  );

  return (
    <>
      <RelatedPaperTitle size="md" bold>
        Related papers
      </RelatedPaperTitle>
      {!relatedPapers && (
        <RelatedPapersSpinnerContainer>
          <Body size="md">Loading...</Body>
        </RelatedPapersSpinnerContainer>
      )}
      {relatedPapers && (
        <RelatedPapersContainer>
          {relatedPapers.map((paper, i) => (
            <ImpressionWatcher
              key={paper.id}
              source="inline-reader-related-works-sidebar"
              entity_id={paper.id}
              extra={{ position: i, source: sourceWorkId }}
              entity_type={ImpressionEntity.Work}
              impressionId={impressionIds[i]}
            >
              <RelatedPaper
                index={i}
                paper={paper}
                impressionId={impressionIds[i]}
                updateWorkBookmarkStatus={updateWorkBookmarkStatus}
              />
            </ImpressionWatcher>
          ))}
        </RelatedPapersContainer>
      )}
    </>
  );
}
